import {
    Box,
    IconButton,
    Stack,
    useTheme,
    alpha,
    Divider,
    Typography,
    Button,
    Popover,
    Drawer,
    useMediaQuery,
} from "@mui/material";
import {useState, useRef, useMemo, useEffect} from "react";
import { useHistory } from "react-router-dom";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { CartIcon } from "../../../../../../assets/header/headerIcon";
import CloseMenuIcon from "../../../../../../assets/menu/closeMenuIcon";
import { useTranslation } from "react-i18next";
import { MENU } from "../../../../../../locales/components/namespaces";
import useCartData from "../../../../../../hooks/cart/useCartData";
import useCartAction from "../../../../../../hooks/cart/useCartAction";

const HeaderCart = () => {
    const { t } = useTranslation(MENU);
    const theme = useTheme();
    const history = useHistory();
    const [isCartIconOpen, setIsCartIconOpen] = useState(false);
    const cartIconRef = useRef(null);
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    const { cartItems, count,shouldRefreshCart } = useCartData();
    const { getCart, editCart,ackCartRefresh } = useCartAction();

    useEffect(() => {
        if (!shouldRefreshCart) return;

        getCart();
        ackCartRefresh();
    }, [shouldRefreshCart, getCart, ackCartRefresh]);

    const cartTotal = useMemo(() => {
        return cartItems
            .reduce((sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 0), 0)
            .toFixed(2);
    }, [cartItems]);

    const handleCartIconClose = () => setIsCartIconOpen(false);

    const handleCartIconToggle = () => {
        if (!isCartIconOpen) {
            getCart();
        }
        setIsCartIconOpen((prev) => !prev);
    };

    const handleRemoveItem = (itemId) => {
        if (!itemId) return;

        editCart({
            id: itemId,
            quantity: 0,
            suppressNotification: true,
        });

    };

    const handleCheckout = () => {
        getCart();
        history.push("/cart");
        handleCartIconClose();
    };

    const listBoxSx = {
        flex: "1 1 auto",
        overflowY: "auto",
        p: "20px",
        "&::-webkit-scrollbar": { width: "8px" },
        "&::-webkit-scrollbar-thumb": {
            backgroundColor: alpha(theme.palette.primary.main, 0.15),
            borderRadius: "4px",
        },
    };

    const renderCartContent = () => (
        <>
            <Box sx={listBoxSx}>
                {cartItems.length === 0 ? (
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            minHeight: "200px",
                        }}
                    >
                        <Typography
                            sx={{
                                color: "#999",
                                fontSize: "14px",
                                fontFamily: "'Roboto Condensed', Arial, sans-serif",
                            }}
                        >
                            {t("empty_cart")}
                        </Typography>
                    </Box>
                ) : (
                    <Stack spacing={0}>
                        {cartItems.map((item, index) => (
                            <Box key={item.id}>
                                <Box
                                    sx={{
                                        display: "flex",
                                        gap: "15px",
                                        alignItems: "flex-start",
                                        py: "10px",
                                    }}
                                >
                                    <Box
                                        component="img"
                                        src={item.image}
                                        alt={item.title}
                                        sx={{
                                            width: "68px",
                                            height: "60px",
                                            objectFit: "contain",
                                            backgroundColor: "#F0F0F0",
                                            borderRadius: "5px",
                                            flexShrink: 0,
                                        }}
                                    />

                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography
                                            sx={{
                                                fontSize: "12px",
                                                fontWeight: 400,
                                                lineHeight: "120%",
                                                mb: "8px",
                                                whiteSpace: "normal",
                                                wordBreak: "break-word",
                                                fontFamily: "'Roboto Condensed', Arial, sans-serif",
                                            }}
                                        >
                                            {item.quantity} x {item.title}
                                        </Typography>

                                        <Typography
                                            sx={{
                                                fontSize: "14px",
                                                fontWeight: 700,
                                                lineHeight: "120%",
                                                fontFamily: "'Roboto Condensed', Arial, sans-serif",
                                            }}
                                        >
                                            {(Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)} €
                                        </Typography>
                                    </Box>

                                    <IconButton
                                        onClick={() => handleRemoveItem(item.id)}
                                        sx={{
                                            color: "#E74C8C",
                                            p: 0,
                                            flexShrink: 0,
                                            "&:hover": {
                                                backgroundColor: alpha("#E74C8C", 0.1),
                                            },
                                        }}
                                    >
                                        <CloseMenuIcon
                                            color="#E74C8C"
                                            width={12}
                                            height={12}
                                            strokeWidth={2.5}
                                        />
                                    </IconButton>
                                </Box>

                                {index < cartItems.length - 1 && (
                                    <Divider sx={{ backgroundColor: "#e0e0e0", my: 1 }} />
                                )}
                            </Box>
                        ))}
                    </Stack>
                )}
            </Box>

            <Divider sx={{ my: 0 }} />

            <Box sx={{ p: "20px", flexShrink: 0, backgroundColor: "#FCFCFD" }}>
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: "20px",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            lineHeight: "90%",
                            color: "#333",
                            fontFamily: "'Roboto Condensed', Arial, sans-serif",
                        }}
                    >
                        {t("total")}
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "14px",
                            fontWeight: 700,
                            lineHeight: "90%",
                            fontFamily: "'Roboto Condensed', Arial, sans-serif",
                        }}
                    >
                        {cartTotal} €
                    </Typography>
                </Box>

                <Button
                    fullWidth
                    onClick={handleCheckout}
                    disabled={cartItems.length === 0}
                    sx={{
                        borderRadius: "25px",
                        border: `2px solid ${theme.palette.primary.main}`,
                        color: theme.palette.primary.main,
                        backgroundColor: "transparent",
                        fontSize: "14px",
                        fontWeight: 900,
                        lineHeight: "120%",
                        py: "12px",
                        fontFamily: "'Zen Maru Gothic', system-ui, sans-serif",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        transition: "all 0.3s ease",
                        "&:disabled": { border: `2px solid #ccc`, color: "#ccc" },
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "8px",
                    }}
                >
                    {t("order_completion")}
                    <KeyboardArrowRightIcon sx={{ fontSize: "18px" }} />
                </Button>
            </Box>
        </>
    );

    return (
        <>
            <IconButton
                ref={cartIconRef}
                onClick={handleCartIconToggle}
                aria-label="open cart"
                sx={{ p: 0, "&:hover": { backgroundColor: "transparent" } }}
            >
                <CartIcon />
            </IconButton>

            {isMobile ? (
                <Drawer
                    anchor="right"
                    open={isCartIconOpen}
                    onClose={handleCartIconClose}
                    PaperProps={{
                        sx: {
                            width: "100%",
                            maxWidth: "420px",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", height: "100vh" }}>
                        <Box sx={{ p: "20px", flexShrink: 0 }}>
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    lineHeight: "90%",
                                    fontFamily: "'Roboto Condensed', Arial, sans-serif",
                                }}
                            >
                                {t("my_cart")} ({count})
                            </Typography>
                        </Box>

                        {renderCartContent()}
                    </Box>
                </Drawer>
            ) : (
                <Popover
                    open={isCartIconOpen}
                    anchorEl={cartIconRef.current}
                    onClose={handleCartIconClose}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    PaperProps={{
                        sx: {
                            borderRadius: "8px",
                            backgroundColor: "#FCFCFD",
                            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                            width: "327px",
                            maxHeight: "calc(100vh - 80px)",
                            display: "flex",
                            flexDirection: "column",
                            overflow: "auto",
                        },
                    }}
                >
                    <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                        <Box
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: "20px",
                                flexShrink: 0,
                            }}
                        >
                            <Typography
                                variant="h6"
                                sx={{
                                    fontWeight: 700,
                                    fontSize: "14px",
                                    lineHeight: "90%",
                                    fontFamily: "'Roboto Condensed', Arial, sans-serif",
                                }}
                            >
                                {t("my_cart")} ({count})
                            </Typography>
                        </Box>

                        {renderCartContent()}
                    </Box>
                </Popover>
            )}
        </>
    );
};

export default HeaderCart;